import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Grid,
} from '@mui/material';

interface FileInfo {
  filename: string;
  status: string;
}

interface FileInfoTableProps {
  eno1Files: FileInfo[];
  e55Files: FileInfo[];
}

function FileInfoTable({ eno1Files, e55Files }: FileInfoTableProps) {
  // Don't render the table if there are no files
  if ((!eno1Files || eno1Files.length === 0) && (!e55Files || e55Files.length === 0)) {
    return null;
  }

  // Helper function to get status chip color
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ mb: 4 }} data-testid="file-info-table-container">
      <Typography variant="h6" sx={{ mb: 2, color: '#063142', fontWeight: 600 }} data-testid="file-info-table-title">
        File Information
      </Typography>
      
      <Grid container spacing={3}>
        {/* eNO1 Files Table */}
        {eno1Files && eno1Files.length > 0 && (
          <Grid item xs={12} md={6}>
            <TableContainer component={Paper} sx={{ maxWidth: '100%' }} data-testid="eno1-files-table-container">
              <Table size="small" data-testid="eno1-files-table">
                <TableHead data-testid="eno1-files-table-head">
                  <TableRow sx={{ backgroundColor: '#E7EFF5' }} data-testid="eno1-files-table-header-row">
                    <TableCell sx={{ fontWeight: 600, color: '#063142' }} data-testid="eno1-files-table-filename-header">
                      eNO1 Files
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#063142', width: '120px' }} data-testid="eno1-files-table-status-header">
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody data-testid="eno1-files-table-body">
                  {eno1Files.map((file, index) => (
                    <TableRow key={index} hover data-testid={`eno1-files-table-row-${index}`}>
                      <TableCell 
                        sx={{ fontFamily: 'monospace', fontSize: '12px' }} 
                        data-testid={`eno1-files-table-filename-cell-${index}`}
                      >
                        {file.filename}
                      </TableCell>
                      <TableCell 
                        sx={{ textAlign: 'center' }} 
                        data-testid={`eno1-files-table-status-cell-${index}`}
                      >
                        {file.status && file.status.trim() !== '' && (
                          <Chip 
                            label={file.status} 
                            color={getStatusColor(file.status)}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}

        {/* E55 Files Table */}
        {e55Files && e55Files.length > 0 && (
          <Grid item xs={12} md={6}>
            <TableContainer component={Paper} sx={{ maxWidth: '100%' }} data-testid="e55-files-table-container">
              <Table size="small" data-testid="e55-files-table">
                <TableHead data-testid="e55-files-table-head">
                  <TableRow sx={{ backgroundColor: '#E7EFF5' }} data-testid="e55-files-table-header-row">
                    <TableCell sx={{ fontWeight: 600, color: '#063142' }} data-testid="e55-files-table-filename-header">
                      E55 Files
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#063142', width: '120px' }} data-testid="e55-files-table-status-header">
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody data-testid="e55-files-table-body">
                  {e55Files.map((file, index) => (
                    <TableRow key={index} hover data-testid={`e55-files-table-row-${index}`}>
                      <TableCell 
                        sx={{ fontFamily: 'monospace', fontSize: '12px' }} 
                        data-testid={`e55-files-table-filename-cell-${index}`}
                      >
                        {file.filename}
                      </TableCell>
                      <TableCell 
                        sx={{ textAlign: 'center' }} 
                        data-testid={`e55-files-table-status-cell-${index}`}
                      >
                        {file.status && file.status.trim() !== '' && (
                          <Chip 
                            label={file.status} 
                            color={getStatusColor(file.status)}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default FileInfoTable;

